/**
 * BreakpointToggle - Responsive preview breakpoint selector
 *
 * Allows users to preview their page at different screen sizes:
 * - Mobile (base): 375px
 * - Tablet: 768px
 * - Desktop: 1200px
 */

import React from 'react';
import { Smartphone, Tablet, Monitor } from 'lucide-react';

/**
 * Breakpoint configuration
 * Matches the breakpoints used in StyleResolver media queries
 *
 * Note: The 'width' here is for responsive CSS media query purposes.
 * The preview canvas uses a separate, wider width (DESKTOP_CANVAS_WIDTH)
 * to accurately simulate a real desktop viewport.
 */
export const BREAKPOINTS = [
  {
    id: 'base',
    label: 'Mobile',
    width: 375,
    icon: Smartphone,
    mediaQuery: null, // Default, no media query needed
  },
  {
    id: 'tablet',
    label: 'Tablet',
    width: 768,
    icon: Tablet,
    mediaQuery: '(min-width: 768px)',
  },
  {
    id: 'desktop',
    label: 'Desktop',
    width: 1200,
    icon: Monitor,
    mediaQuery: '(min-width: 1024px)',
  },
];

// Desktop canvas width for preview - simulates a real desktop viewport
// This is separate from the breakpoint width (which is for responsive CSS)
export const DESKTOP_CANVAS_WIDTH = 1440;

/**
 * Get breakpoint configuration by ID
 * @param {string} id - Breakpoint ID
 * @returns {Object|undefined} Breakpoint config
 */
export function getBreakpoint(id) {
  return BREAKPOINTS.find((bp) => bp.id === id);
}

/**
 * BreakpointToggle - Toggle button group for breakpoint selection
 *
 * @param {Object} props
 * @param {string} props.activeBreakpoint - Currently selected breakpoint ID
 * @param {Function} props.onChange - Callback when breakpoint changes: (breakpointId) => void
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.size='md'] - Button size: 'sm' | 'md' | 'lg'
 * @param {boolean} [props.showLabels=false] - Show text labels alongside icons
 * @param {boolean} [props.showWidths=false] - Show width values on hover
 */
export default function BreakpointToggle({
  activeBreakpoint,
  onChange,
  className = '',
  size = 'md',
  showLabels = false,
  showWidths = false,
}) {
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className={`inline-flex rounded-md shadow-sm ${className}`} role="group">
      {BREAKPOINTS.map((breakpoint, index) => {
        const isActive = activeBreakpoint === breakpoint.id;
        const Icon = breakpoint.icon;

        const isFirst = index === 0;
        const isLast = index === BREAKPOINTS.length - 1;

        return (
          <button
            key={breakpoint.id}
            type="button"
            onClick={() => onChange(breakpoint.id)}
            className={`
              ${sizeClasses[size]}
              inline-flex items-center justify-center gap-2
              border border-gray-300
              transition-colors duration-150
              ${isFirst ? 'rounded-l-md' : ''}
              ${isLast ? 'rounded-r-md' : ''}
              ${!isFirst ? '-ml-px' : ''}
              ${isActive
                ? 'bg-blue-500 text-white border-blue-500 z-10'
                : 'bg-white text-gray-700 hover:bg-gray-50'
              }
              focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
            `}
            title={showWidths ? `${breakpoint.label} (${breakpoint.width}px)` : breakpoint.label}
            aria-pressed={isActive}
          >
            <Icon className={iconSizes[size]} />
            {showLabels && (
              <span className="text-sm font-medium">{breakpoint.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * BreakpointToggleDropdown - Alternative dropdown-style selector
 * Useful when horizontal space is limited
 *
 * @param {Object} props
 * @param {string} props.activeBreakpoint - Currently selected breakpoint ID
 * @param {Function} props.onChange - Callback when breakpoint changes
 * @param {string} [props.className] - Additional CSS classes
 */
export function BreakpointToggleDropdown({
  activeBreakpoint,
  onChange,
  className = '',
}) {
  const activeConfig = getBreakpoint(activeBreakpoint) || BREAKPOINTS[0];
  const ActiveIcon = activeConfig.icon;

  return (
    <div className={`relative inline-block ${className}`}>
      <select
        value={activeBreakpoint}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none pl-8 pr-8 py-2 border border-gray-300 rounded-md bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {BREAKPOINTS.map((bp) => (
          <option key={bp.id} value={bp.id}>
            {bp.label} ({bp.width}px)
          </option>
        ))}
      </select>
      <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
        <ActiveIcon className="w-4 h-4" />
      </div>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

/**
 * BreakpointIndicator - Shows current breakpoint info (for status bars)
 *
 * @param {Object} props
 * @param {string} props.breakpoint - Current breakpoint ID
 * @param {string} [props.className] - Additional CSS classes
 */
export function BreakpointIndicator({ breakpoint, className = '' }) {
  const config = getBreakpoint(breakpoint) || BREAKPOINTS[0];
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 text-gray-600 ${className}`}>
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium">
        {config.label}
        <span className="text-gray-400 ml-1">({config.width}px)</span>
      </span>
    </div>
  );
}
